import React from "react";
import { Link, useSearchParams } from "react-router-dom";

import { bemClassName } from "../../utils/bemClassName";
import composePaginationLinkTo from "./composePaginationLinkTo";
import getNextOffset from "./getNextOffset";
import getPageNumber from "./getPageNumber";
import getPreviousOffset from "./getPreviousOffset";
import { joinClassNames } from "../../utils/joinClassNames";
import { useSearchQuestion, useSearchResults } from "../../context/DataContext";

import { ItemTypeValues } from "./PaginationItem";
import styles from "./PaginationItem.module.css";

export type DirectionValues = "next" | "previous";

export type PaginationControlProps = {
    direction: DirectionValues;
    itemType: ItemTypeValues;
    stepLabel?: string;
};

export const PaginationControl = ({
    direction = "previous",
    itemType = "link",
    stepLabel = "page",
}: PaginationControlProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        pagination,
        resultsSummary: summary,
        currentStartRank,
    } = useSearchResults();
    const { setPage } = useSearchQuestion();
    const { limit } = pagination;

    const directionModifier = direction === "previous" ? "prev" : "next";
    const cssBlockName = "ripple-news-listing";
    const controlClasses = joinClassNames([
        bemClassName(cssBlockName, "pagination-control teal"),
        bemClassName(cssBlockName, "pagination", directionModifier),
        styles.paginationArrow,
    ]);

    const labelPrefix = direction === "previous" ? "Previous" : "Next";
    const label = (
        <span className="visuallyhidden">
            {labelPrefix.trim()} {stepLabel.trim()}
        </span>
    );

    const currentPageOffset = currentStartRank;
    const currentPageNumber = getPageNumber(currentPageOffset, limit);
    const newPageNumber =
        direction === "previous"
            ? currentPageNumber - 1
            : currentPageNumber + 1;
    const newPageOffset =
        direction === "previous"
            ? getPreviousOffset(currentPageOffset, limit)
            : getNextOffset(currentPageOffset, limit, summary.totalMatching);
    const isLink = itemType === "link";
    const linkTo = isLink
        ? composePaginationLinkTo(newPageNumber, limit, searchParams)
        : "";

    const handleLinkClick = () => {
        setPage(newPageNumber);
    };

    const handleButtonClick = () => {
        const newSearchParams = new URLSearchParams(searchParams);
        setPage(newPageNumber);
        if (newPageOffset) {
            newSearchParams.set("start_rank", String(newPageOffset));
            setSearchParams(newSearchParams);
        }
    };

    return isLink ? (
        <Link to={linkTo} className={controlClasses} onClick={handleLinkClick}>
            {label}
        </Link>
    ) : (
        <button
            type="button"
            className={controlClasses}
            onClick={handleButtonClick}
        >
            {label}
        </button>
    );
};

export default PaginationControl;
